import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, ArrowUp, ArrowDown, ImagePlus, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SECTIONS = [
  { id: "activities", label: "Activities (What We Do)" },
  { id: "trips", label: "Trips out (What We Do)" },
  { id: "life-skills", label: "Life skills (How We Support)" },
];

const resolveSrc = (src) => (src?.startsWith("http") ? src : `${BACKEND}${src}`);

function GalleryItem({ item, first, last, onChanged }) {
  const [alt, setAlt] = useState(item.alt);
  const [saving, setSaving] = useState(false);

  const saveAlt = async () => {
    if (alt.trim() === item.alt) return;
    setSaving(true);
    try {
      await api.put(`/admin/gallery/${item.id}`, { alt: alt.trim() });
      toast.success("Description updated.");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const move = async (direction) => {
    try {
      await api.post(`/admin/gallery/${item.id}/move`, { direction });
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this photo from the website? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/gallery/${item.id}`);
      toast.success("Photo removed.");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-3" data-testid={`gallery-item-${item.id}`}>
      <img src={resolveSrc(item.src)} alt={item.alt} className="h-40 w-full rounded-xl object-cover" loading="lazy" />
      <div className="mt-3 flex gap-2">
        <input
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          onBlur={saveAlt}
          aria-label="Photo description"
          className="h-10 w-full rounded-lg border-2 border-stone-200 px-2 text-sm focus-visible:border-orange-600"
          data-testid={`gallery-alt-input-${item.id}`}
        />
        {saving && <Loader2 className="h-5 w-5 animate-spin text-stone-400 self-center" />}
        {!saving && alt.trim() !== item.alt && (
          <button onClick={saveAlt} className="rounded-lg p-2 text-green-700 hover:bg-green-50" aria-label="Save description" data-testid={`gallery-alt-save-${item.id}`}>
            <Check className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="flex gap-1">
          <button onClick={() => move("up")} disabled={first} className="rounded-lg p-2 text-stone-600 hover:bg-stone-100 disabled:opacity-30" aria-label="Move earlier" data-testid={`gallery-move-up-${item.id}`}>
            <ArrowUp className="h-5 w-5" />
          </button>
          <button onClick={() => move("down")} disabled={last} className="rounded-lg p-2 text-stone-600 hover:bg-stone-100 disabled:opacity-30" aria-label="Move later" data-testid={`gallery-move-down-${item.id}`}>
            <ArrowDown className="h-5 w-5" />
          </button>
        </div>
        <button onClick={remove} className="rounded-lg p-2 text-stone-500 hover:bg-red-50 hover:text-red-700" aria-label="Delete photo" data-testid={`gallery-delete-${item.id}`}>
          <Trash2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

export default function GalleryTab() {
  const [section, setSection] = useState("activities");
  const [gallery, setGallery] = useState(null);
  const [queue, setQueue] = useState([]); // [{ id, file, preview, alt }]
  const [progress, setProgress] = useState(null); // { done, total }
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => setGallery({}));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const addFiles = (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    const next = chosen.map((file) => ({
      id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      preview: URL.createObjectURL(file),
      alt: "",
    }));
    setQueue((q) => [...q, ...next]);
  };

  const updateAlt = (id, value) => {
    setQueue((q) => q.map((item) => (item.id === id ? { ...item, alt: value } : item)));
  };

  const removeFromQueue = (id) => {
    setQueue((q) => {
      const target = q.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return q.filter((item) => item.id !== id);
    });
  };

  const clearQueue = () => {
    queue.forEach((item) => URL.revokeObjectURL(item.preview));
    setQueue([]);
  };

  const uploadAll = async (e) => {
    e.preventDefault();
    if (!queue.length) return toast.error("Please choose one or more photos to upload.");
    const missing = queue.filter((item) => !item.alt.trim());
    if (missing.length) {
      return toast.error("Please add a short description to every photo (needed for accessibility).");
    }

    setProgress({ done: 0, total: queue.length });
    let ok = 0;
    const succeededIds = [];
    const failures = [];
    for (let i = 0; i < queue.length; i += 1) {
      const item = queue[i];
      try {
        const fd = new FormData();
        fd.append("section", section);
        fd.append("alt", item.alt.trim());
        fd.append("image", item.file);
        await api.post("/admin/gallery", fd);
        ok += 1;
        succeededIds.push(item.id);
      } catch (err) {
        const detail = formatApiErrorDetail(err.response?.data?.detail) || "upload failed";
        failures.push(`${item.file.name} (${detail})`);
      }
      setProgress({ done: i + 1, total: queue.length });
    }

    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added to the website.`);
    if (failures.length) toast.error(`Could not upload: ${failures.join("; ")}. These are still in the list — please try again.`);

    // Keep any failed items (with their descriptions); drop the ones that succeeded.
    setQueue((q) => {
      q.filter((item) => succeededIds.includes(item.id)).forEach((item) => URL.revokeObjectURL(item.preview));
      return q.filter((item) => !succeededIds.includes(item.id));
    });
    setProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    load();
  };

  const items = gallery?.[section] || [];
  const uploading = progress !== null;

  return (
    <div data-testid="gallery-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold ${section === s.id ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-100"}`}
            data-testid={`gallery-section-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <form onSubmit={uploadAll} className="rounded-2xl bg-white border border-stone-200 p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[220px]">
            <label htmlFor="gallery-file-input" className="block text-base font-medium text-stone-900 mb-1">
              Photos <span className="font-normal text-stone-500">(choose one or several)</span>
            </label>
            <input
              id="gallery-file-input"
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              onChange={(e) => addFiles(e.target.files)}
              className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-orange-600 file:px-4 file:py-2 file:text-white file:font-semibold"
              data-testid="gallery-upload-input"
            />
          </div>
          {queue.length > 0 && !uploading && (
            <button
              type="button"
              onClick={clearQueue}
              className="inline-flex h-11 items-center gap-2 px-4 rounded-xl border-2 border-stone-300 text-stone-700 font-semibold hover:bg-stone-100"
              data-testid="gallery-clear-button"
            >
              Clear all
            </button>
          )}
        </div>

        {queue.length > 0 && (
          <div className="mt-5 space-y-3" data-testid="gallery-upload-queue">
            {queue.map((item) => (
              <div key={item.id} className="flex items-center gap-3 rounded-xl border border-stone-200 p-2" data-testid={`gallery-queue-item-${item.id}`}>
                <img src={item.preview} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />
                <input
                  value={item.alt}
                  onChange={(e) => updateAlt(item.id, e.target.value)}
                  disabled={uploading}
                  placeholder="Short description, e.g. Someone we support baking a cake"
                  aria-label={`Description for ${item.file.name}`}
                  className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600 disabled:bg-stone-50"
                  data-testid={`gallery-queue-alt-${item.id}`}
                />
                {!uploading && (
                  <button
                    type="button"
                    onClick={() => removeFromQueue(item.id)}
                    className="shrink-0 rounded-lg p-2 text-stone-500 hover:bg-red-50 hover:text-red-700"
                    aria-label={`Remove ${item.file.name} from upload`}
                    data-testid={`gallery-queue-remove-${item.id}`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center gap-4">
          <button
            type="submit"
            disabled={uploading || queue.length === 0}
            className="inline-flex h-11 items-center gap-2 px-5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-60"
            data-testid="gallery-upload-button"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            {uploading
              ? `Uploading ${progress.done} of ${progress.total}…`
              : queue.length > 1
              ? `Add ${queue.length} photos`
              : "Add photo"}
          </button>
          {queue.length > 0 && !uploading && (
            <span className="text-sm text-stone-500">Uploading to: {SECTIONS.find((s) => s.id === section)?.label}</span>
          )}
        </div>
      </form>

      {gallery === null ? (
        <div className="flex items-center gap-2 text-stone-600 py-10">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading photos…
        </div>
      ) : items.length === 0 ? (
        <p className="text-stone-600 py-6">No photos in this section yet.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
          {items.map((item, i) => (
            <GalleryItem key={item.id} item={item} first={i === 0} last={i === items.length - 1} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
